<script setup>
import { ref, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const baseUrl     = import.meta.env.VITE_CHATWOOT_BASE_URL
const websiteToken = import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN
const enabled     = Boolean(baseUrl && websiteToken)

const loaded = ref(false)
const loading = ref(false)
let cleanupListener = null

function injectScript() {
  return new Promise((resolve, reject) => {
    if (window.chatwootSDK) return resolve()
    const s = document.createElement('script')
    s.src = `${baseUrl}/packs/js/sdk.js`
    s.defer = true
    s.async = true
    s.onload  = () => resolve()
    s.onerror = () => reject(new Error('Chatwoot SDK failed to load'))
    document.body.appendChild(s)
  })
}

function whenReady(fn) {
  if (window.$chatwoot) return fn()
  const handler = () => { window.removeEventListener('chatwoot:ready', handler); fn() }
  window.addEventListener('chatwoot:ready', handler)
  cleanupListener = () => window.removeEventListener('chatwoot:ready', handler)
}

async function open() {
  if (!enabled || loading.value) return
  if (loaded.value) { window.$chatwoot?.toggle('open'); return }
  loading.value = true
  try {
    await injectScript()
    window.chatwootSDK.run({ websiteToken, baseUrl })
    whenReady(() => {
      window.$chatwoot.toggle('open')
      loaded.value = true
      loading.value = false
    })
  } catch (e) {
    loading.value = false
    console.warn('[ChatwootWidget]', e)
  }
}

onUnmounted(() => { cleanupListener?.() })
</script>

<template>
  <button v-if="enabled" type="button" class="cw-bubble" :disabled="loading" @click="open">
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
    </svg>
    <span class="cw-bubble__label">{{ t('public.widget.cta') }}</span>
  </button>
</template>

<style scoped>
.cw-bubble {
  position: fixed; right: 1rem; bottom: max(env(safe-area-inset-bottom), 1rem);
  display: inline-flex; align-items: center; gap: 0.5rem;
  padding: 0.65rem 1rem 0.65rem 0.85rem;
  background: var(--accent); color: var(--accentInk);
  border: 0; border-radius: 999px;
  font: inherit; font-weight: 600; font-size: 0.875rem;
  cursor: pointer; z-index: 60;
  box-shadow: 0 10px 25px -5px color-mix(in oklch, var(--accent) 50%, transparent);
  transition: transform 150ms ease;
}
.cw-bubble:hover    { transform: translateY(-1px); }
.cw-bubble:disabled { opacity: 0.7; cursor: progress; }
@media (max-width: 480px) { .cw-bubble__label { display: none; } .cw-bubble { padding: 0.7rem; } }
</style>
